import React, { useEffect, useRef } from "react";
import { TopoMap, GoldOrb, StarField, PaperGrain } from "./TopographicBackground";

const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_tactile-book/artifacts/mzforwzs_braced%20no%20more%20facebook%20post%20image.png";

/**
 * SpatialTopography — layered topographic-map depth with pointer parallax.
 * Far layers are large + blurred + slow; near layers are crisp + faster,
 * creating a calm sense of three-dimensional space behind the content.
 */
export const SpatialTopography = ({ variant = "reader", className = "" }) => {
  const farRef = useRef(null);
  const midRef = useRef(null);
  const nearRef = useRef(null);
  const orbRef = useRef(null);
  const logoRef = useRef(null);
  const frame = useRef(0);
  const target = useRef({ x: 0, y: 0 });
  const cur = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      target.current = { x: e.clientX / w - 0.5, y: e.clientY / h - 0.5 };
    };
    const tick = () => {
      cur.current.x += (target.current.x - cur.current.x) * 0.05;
      cur.current.y += (target.current.y - cur.current.y) * 0.05;
      const { x, y } = cur.current;
      if (farRef.current)
        farRef.current.style.transform = `translate3d(${x * -14}px, ${y * -10}px, 0) scale(1.06)`;
      if (midRef.current)
        midRef.current.style.transform = `translate3d(${x * 26}px, ${y * 18}px, 0)`;
      if (nearRef.current)
        nearRef.current.style.transform = `translate3d(${x * 46}px, ${y * 32}px, 0) scale(1.04)`;
      if (orbRef.current)
        orbRef.current.style.transform = `translate3d(${x * 34}px, ${y * 24}px, 0)`;
      if (logoRef.current)
        logoRef.current.style.transform = `translate3d(${x * 18}px, ${y * 12}px, 0)`;
      frame.current = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", onMove);
    frame.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(frame.current);
    };
  }, []);

  const strong = variant === "veil";

  return (
    <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} data-testid="spatial-topography">
      <PaperGrain />
      {/* Embedded brand logo watermark */}
      <div ref={logoRef} className="absolute inset-0 flex items-center justify-center will-change-transform">
        <div
          className="h-full w-full"
          style={{
            backgroundImage: `url("${LOGO_URL}")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: strong ? "min(1200px, 94vw) auto" : "min(1500px, 96vw) auto",
            opacity: strong ? 0.16 : 0.12,
            mixBlendMode: "multiply",
          }}
        />
      </div>
      {/* Far, blurred contour field */}
      <div ref={farRef} className="absolute -inset-[8%] will-change-transform" style={{ filter: "blur(3px)" }}>
        <TopoMap className="h-full w-full" opacity={strong ? 0.28 : 0.2} seed={3} testId="topo-far" />
      </div>
      {/* Luminous orb */}
      <div ref={orbRef} className="absolute inset-0 will-change-transform">
        <GoldOrb
          className={`left-1/2 -translate-x-1/2 ${strong ? "top-[6%]" : "top-[-12%]"}`}
          size={strong ? 640 : 560}
          testId="spatial-orb"
        />
      </div>
      {/* Mid contour field */}
      <div ref={midRef} className="absolute inset-0 will-change-transform">
        <TopoMap className="h-full w-full" opacity={strong ? 0.5 : 0.42} seed={7} testId="topo-mid" />
      </div>
      {/* Near, crisp accents */}
      <div ref={nearRef} className="absolute -inset-[4%] will-change-transform">
        <TopoMap className="h-full w-full" opacity={strong ? 0.42 : 0.3} seed={12} testId="topo-near" />
      </div>
      <StarField count={strong ? 22 : 14} className={strong ? "opacity-70" : "opacity-50"} />
    </div>
  );
};
